'use client';

import type { ReactNode } from 'react';
import cs from 'classnames';
import { styled } from 'styled-components';
import {
  accordionSchemeDark,
  accordionSchemeLight,
} from '@dvd/styles/disclosure';

type AccordionListProps = {
  /**
   * Accordion components passed in as children
   */
  children: ReactNode;
  /**
   * Optional class name, nesc. for extending as a styled-component
   */
  className?: string;
  /**
   * Color variant for the whole list. `dark` renders navy cards with white
   * text for dark sections (e.g. a `blueDark` `Section`).
   *
   * The value reaches the cards as inherited custom properties, so children
   * can be nested, fragmented, or mapped however a consumer likes. An
   * individual `Accordion` can still opt out with its own `variant`.
   */
  variant?: 'light' | 'dark';
};

const AccordionListWrapper = styled.ul`
  /*
   * Publishes the --accordion-* contract that every card inside reads. Set
   * here rather than pushed into children, so the list never has to know what
   * its children are.
   */
  ${accordionSchemeLight}

  border-radius: ${({ theme }) => theme.radius.std};
  box-shadow: ${({ theme }) => theme.elevation.elevation2()};
  height: fit-content;
  margin-top: ${({ theme }) => theme.spacing['1.5']};
  overflow: hidden;
  padding: 0;

  &.dark {
    ${accordionSchemeDark}
  }

  & > :not(:last-child) {
    border-bottom: 1px solid
      var(--accordion-border, ${({ theme }) => theme.colors.grey100});
  }

  & > li {
    list-style-type: none;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    margin: 0;
  }
`;

const AccordionList = ({
  children,
  className,
  variant = 'light',
}: AccordionListProps) => (
  <AccordionListWrapper className={cs(className, { dark: variant === 'dark' })}>
    {children}
  </AccordionListWrapper>
);

export default AccordionList;
